// What every typed widget shares (spec/dashboard.md section 6.4): the props, the editor host (one stack of open documents
// in the slide-over, section 6.2: a page's open replaces it, an open from inside the editor pushes), and the helpers that
// stage a new document from a template's defaults.
import { createContext, useContext } from 'react';
import { useParams } from 'react-router';
import type { ApiClient } from '../../api/client';
import type { Json, Parameter, Template } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { useWorkspace } from '../../workspace/react';
import type { Workspace } from '../../workspace/Workspace';
import { useContent, type ContentCatalog } from '../ContentContext';
import { typeDefault } from '../schema';

export interface WidgetProps {
  param: Parameter;
  value: Json | undefined;
  /** one value per user intent (blur / Enter / pick / toggle), never per keystroke */
  onCommit: (value: Json) => void;
  /** a commit at a path inside the value (`amount`, `[2]`, `[0].item`): one change per leaf (section 4.1); the whole value is committed when absent */
  onCommitAt?: (relativePath: string, value: Json) => void;
  disabled?: boolean;
  /** a grid cell: the small, single-line form */
  compact?: boolean;
  autoFocus?: boolean;
  id?: string;
  onCancel?: () => void;
  /** the document that owns the field (components it owns, "edit in document" from a cell) */
  owner?: string;
}

/** Where a stacked entry came from: the parent document (the entry below it by default), the field and its label (the crumb). */
export interface EditorOrigin {
  parentId?: string;
  path?: string;
  label?: string;
}

/** One entry of the editor's stack (section 6.2): the root is the `?doc=` of the URL, the ones above it are drafts only. */
export interface EditorStackEntry {
  id: string;
  origin?: EditorOrigin;
}

export interface EditorOpenOptions {
  /** Cancel of a pushed entry reverts to this mark (a picker's "+ Create" takes it before it creates the draft) */
  cancelMark?: number;
  origin?: EditorOrigin;
}

export interface EditorHost {
  /**
   * Opens the editor of a document. From a page it replaces the stack with `[id]` (and resolves at once); from inside the
   * editor it pushes `{ id, origin }` and resolves when that entry pops: 'confirmed' on Done (or a crumb click below it),
   * 'cancelled' on Cancel (its edits since the mark reverted) or Delete.
   */
  open: (id: string, opts?: EditorOpenOptions) => Promise<'confirmed' | 'cancelled'>;
  /** the open stack, root first (empty when the editor is closed) */
  stack?: readonly EditorStackEntry[];
}

export const EditorHostContext = createContext<EditorHost | null>(null);

export function useEditorHost(): EditorHost | null {
  return useContext(EditorHostContext);
}

export interface WidgetEnv {
  client: ApiClient;
  org: string;
  game: string;
  gameId: string;
  branch: string;
  catalog: ContentCatalog;
  workspace: Workspace;
  host: EditorHost | null;
}

export function useWidgetEnv(): WidgetEnv {
  const { client } = useAuth();
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  return { client, org, game: game.slug, gameId: game.id, branch, catalog: useContent(), workspace: useWorkspace(), host: useEditorHost() };
}

/** A new document of a template: every effective parameter at its default (research/02 section 4 #13). */
export function freshDocument(template: Template, data: Record<string, Json> = {}): Json {
  const values: Record<string, Json> = {};
  for (const p of template.effectiveParameters) values[p.name] = p.name in data ? data[p.name]! : typeDefault(p);
  return { id: '', unnyId: null, template: template.name, export: true, tags: [], legacyFlags: 0, data: values, legacy: null, meta: null, version: 0 } as unknown as Json;
}

/** Stages a new document and returns its temp id; the Changes panel lists only the fields that differ from the defaults (T2). */
export function createDocumentDraft(workspace: Workspace, template: Template, data: Record<string, Json> = {}): string {
  const defaults = freshDocument(template);
  const value = Object.keys(data).length > 0 ? freshDocument(template, data) : defaults;
  return workspace.create('document', value, { label: `New ${template.displayName ?? template.name.split('.').pop()}`, template: template.name, defaults });
}

/** The templates that inherit `name` (any depth), from the catalog. */
export function heirsOf(catalog: ContentCatalog, name: string): Template[] {
  const out: Template[] = [];
  for (const t of catalog.templateList) {
    let p: string | null | undefined = t.parent;
    const seen = new Set<string>();
    while (p && !seen.has(p)) {
      if (p === name) {
        out.push(t);
        break;
      }
      seen.add(p);
      p = catalog.templates.get(p)?.parent;
    }
  }
  return out;
}

/** The template a reference parameter points at and its heirs, concrete ones only (not abstract). */
export function targetsOf(catalog: ContentCatalog, param: Pick<Parameter, 'template'>): Template[] {
  if (!param.template) return catalog.templateList.filter((t) => (t.kind === 'document' || t.kind === 'singleton' || t.kind === 'component') && !t.flags.includes('abstract'));
  const base = catalog.templates.get(param.template);
  const all = base ? [base, ...heirsOf(catalog, base.name)] : [];
  return all.filter((t) => !t.flags.includes('abstract'));
}

export function isComponentTarget(catalog: ContentCatalog, param: Pick<Parameter, 'template' | 'type'>): boolean {
  return param.type === 'document' && !!param.template && catalog.templates.get(param.template)?.kind === 'component';
}

/** `rel` joined under `prefix`: `data.price` + `amount` → `data.price.amount`; `items` + `[2]` → `items[2]`. */
export function joinPath(prefix: string, rel: string): string {
  if (!rel) return prefix;
  return rel.startsWith('[') ? prefix + rel : `${prefix}.${rel}`;
}
